// components/process-units/Stabilisation.js
import React, { useState } from 'react';

const Stabilisation = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Stabilisation</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Stabilisation removes light components from the liquid hydrocarbon stream to produce a stable
            condensate with a controlled vapor pressure. This process typically involves heating, flashing, 
            and/or fractionation to achieve the desired product specifications.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Stabiliser Type</label>
              <select 
                value={config.stabiliserType || 'flash'}
                onChange={(e) => updateConfig({ stabiliserType: e.target.value })}
              >
                <option value="flash">Flash Drum</option>
                <option value="column">Stabiliser Column</option>
                <option value="multistage">Multi-stage Flash</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Operating Temperature (°C)</label>
              <input
                type="number"
                min="0"
                max="200"
                step="1"
                value={config.temperature || 80}
                onChange={(e) => handleInputChange('temperature', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Operating Pressure (bar)</label>
              <input
                type="number"
                min="1"
                max="50"
                step="0.5"
                value={config.pressure || 5}
                onChange={(e) => handleInputChange('pressure', e.target.value)}
              />
            </div>
            
            {config.stabiliserType === 'column' && (
              <>
                <div className="parameter-row">
                  <label>Number of Trays</label>
                  <input
                    type="number"
                    min="5"
                    max="50"
                    step="1"
                    value={config.trayCount || 15}
                    onChange={(e) => handleInputChange('trayCount', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Reflux Ratio</label>
                  <input
                    type="number"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={config.refluxRatio || 1.5}
                    onChange={(e) => handleInputChange('refluxRatio', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>Heating Medium</label>
              <select 
                value={config.heatingMedium || 'steam'}
                onChange={(e) => updateConfig({ heatingMedium: e.target.value })}
              >
                <option value="steam">Steam</option>
                <option value="hotOil">Hot Oil</option>
                <option value="electric">Electric</option>
                <option value="directFired">Direct Fired</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Cooling Medium</label>
              <select 
                value={config.coolingMedium || 'air'}
                onChange={(e) => updateConfig({ coolingMedium: e.target.value })}
              >
                <option value="air">Air</option>
                <option value="water">Water</option>
                <option value="refrigerant">Refrigerant</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>RVP Target (psi)</label>
              <input
                type="number"
                min="5"
                max="15"
                step="0.1"
                value={config.rvpTarget || 10}
                onChange={(e) => handleInputChange('rvpTarget', e.target.value)}
              />
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>Flash stabilisation is simpler but less efficient than column stabilisation.</li>
              <li>The RVP (Reid Vapor Pressure) target affects the energy consumption - lower RVP requires more stripping.</li>
              <li>Stabiliser columns typically have reboilers and condensers, both contributing to energy consumption.</li>
              <li>Higher reflux ratios increase separation efficiency but also increase energy consumption.</li>
              <li>The off-gas from stabilisation can be used as fuel or compressed and returned to the process.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stabilisation;